import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Lightweight, app-wide tooltip with title + explanation + optional shortcut.
 *
 * Wraps a single child element and binds pointer/focus listeners directly to
 * it (no extra wrapper DOM node — preserves layout). Renders the bubble in a
 * portal anchored above (or below, when there's no room above) the target,
 * auto-flipping horizontally to stay inside the viewport.
 *
 * Use the global "Hilfe-Modus" toggle (window.__allyverHelpMode__) to skip the
 * hover delay entirely and surface tips immediately while the user is learning.
 */
export type TooltipProps = {
  /** Short bold heading (one line). */
  title: string;
  /** Optional longer explanation rendered below the title. */
  description?: string;
  /** Optional keyboard shortcut hint (e.g. "Space", "Cmd+D"). */
  shortcut?: string;
  /** Hover delay before showing. Defaults to 350ms (0 in help mode). */
  delayMs?: number;
  /** Preferred placement. Defaults to "top". */
  placement?: "top" | "bottom";
  /** Disable tooltip entirely. */
  disabled?: boolean;
  children: React.ReactElement;
};

// Read the global flag without making the component depend on a context.
function isHelpMode(): boolean {
  if (typeof window === "undefined") return false;
  return Boolean((window as unknown as { __allyverHelpMode__?: boolean }).__allyverHelpMode__);
}

// Singleton portal root — created lazily so SSR doesn't blow up.
function ensurePortalRoot(): HTMLElement {
  let root = document.getElementById("allyver-tooltip-root");
  if (!root) {
    root = document.createElement("div");
    root.id = "allyver-tooltip-root";
    document.body.appendChild(root);
  }
  return root;
}

export const Tooltip: React.FC<TooltipProps> = ({
  title,
  description,
  shortcut,
  delayMs,
  placement = "top",
  disabled = false,
  children,
}) => {
  const id = useId();
  const childRef = useRef<HTMLElement | null>(null);
  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ x: number; y: number; place: "top" | "bottom" } | null>(null);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);

  const show = (): void => {
    if (disabled) return;
    const wait = delayMs ?? (isHelpMode() ? 0 : 350);
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setOpen(true), wait);
  };
  const hide = (): void => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = null;
    setOpen(false);
  };

  // Position the bubble after it mounts and whenever the viewport changes.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const el = childRef.current;
    const bubble = bubbleRef.current;
    if (!el || !bubble) return;

    const place = (): void => {
      const r = el.getBoundingClientRect();
      const bw = bubble.offsetWidth;
      const bh = bubble.offsetHeight;
      const margin = 8;
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      let p: "top" | "bottom" = placement;
      if (p === "top" && r.top - bh - margin < 4) p = "bottom";
      else if (p === "bottom" && r.bottom + bh + margin > vh - 4) p = "top";

      const y = p === "top" ? r.top - bh - margin : r.bottom + margin;
      let x = r.left + r.width / 2 - bw / 2;
      x = Math.max(8, Math.min(vw - bw - 8, x));
      setPos({ x, y, place: p });
    };
    place();
    const onResize = (): void => place();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [open, placement, title, description]);

  // Hide on Escape and on global pointerdown anywhere (so tooltips never get
  // "stuck" if the underlying element is removed mid-hover).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") hide();
    };
    const onDown = (): void => hide();
    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", onDown, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onDown, true);
    };
  }, [open]);

  // Clone child to attach listeners + ref without an extra DOM wrapper.
  const child = React.Children.only(children);
  const childProps = (child.props ?? {}) as Record<string, unknown>;

  const cloned = React.cloneElement(
    child as React.ReactElement<Record<string, unknown>>,
    {
      ref: (node: HTMLElement | null) => {
        childRef.current = node;
        // Forward to any existing ref the child had.
        const orig = (child as unknown as { ref?: React.Ref<HTMLElement> }).ref;
        if (typeof orig === "function") orig(node);
        else if (orig && typeof orig === "object")
          (orig as React.MutableRefObject<HTMLElement | null>).current = node;
      },
      onMouseEnter: (e: React.MouseEvent) => {
        (childProps.onMouseEnter as ((e: React.MouseEvent) => void) | undefined)?.(e);
        show();
      },
      onMouseLeave: (e: React.MouseEvent) => {
        (childProps.onMouseLeave as ((e: React.MouseEvent) => void) | undefined)?.(e);
        hide();
      },
      onFocus: (e: React.FocusEvent) => {
        (childProps.onFocus as ((e: React.FocusEvent) => void) | undefined)?.(e);
        show();
      },
      onBlur: (e: React.FocusEvent) => {
        (childProps.onBlur as ((e: React.FocusEvent) => void) | undefined)?.(e);
        hide();
      },
      "aria-describedby": open ? id : (childProps["aria-describedby"] as string | undefined),
    },
  );

  if (!open || typeof document === "undefined") return cloned;

  const portalRoot = ensurePortalRoot();
  const bubble = (
    <div
      ref={bubbleRef}
      id={id}
      role="tooltip"
      className={`allyver-tooltip ${pos?.place ?? "top"}`}
      style={
        pos
          ? { transform: `translate3d(${pos.x}px, ${pos.y}px, 0)`, opacity: 1 }
          : { opacity: 0 }
      }
    >
      <div className="allyver-tooltip-title">{title}</div>
      {description ? <div className="allyver-tooltip-desc">{description}</div> : null}
      {shortcut ? (
        <div className="allyver-tooltip-shortcut">
          <kbd>{shortcut}</kbd>
        </div>
      ) : null}
    </div>
  );

  return (
    <>
      {cloned}
      {createPortal(bubble, portalRoot)}
    </>
  );
};
